<Header {isExternal} />
<Page contentAreaLayout="wide">
    <PageHeader title={lang.main('Course_catalogue_section_header')} backLinkText={isExternal?lang.main('Course_catalogue_Back_to_login_page'):lang.main('Course_catalogue_Back_to_courses')} backLinkUrl={backUrl} />

    <div class="its-course-catalogue__banners">
        <Banner type='info' visible={!!infoFeedback} text={infoFeedback} closable={true} close={lang.main('Close')} />
        <Banner type='warning' visible={!!errorFeedback} text={errorFeedback} closable={true} close={lang.main('Close')} />
    </div>

    <div class="its-course-catalogue-filter-block" bind:this={searchInputRef}>
        <SearchInput 
            id='local-course-search'
            placeholder={lang.main('Search')}
            label={lang.main('Search_courses')}
            announceResultText={true}
            resultText={loadInProgress || !courses.currentFilter.searchText ? '' : formatLanguageString(lang.main('Course_catalogue_x_courses_found'), courses.totalCount.toString())}
            bind:value={courses.currentFilter.searchText}
            on:keyup={onSearchKeyUp}
            on:clear={onSearchClear}
        />
    </div>
    <div class="its-course-catalogue-filter-block">
        {#if subjects && subjects.length > 1}
            <Select label={lang.main('Course_catalogue_subject')} bind:value={courses.currentFilter.subjectId} labelPosition='top' on:change={() => search(true)}>
                {#each subjects as subject}
                    <option value={subject.id}>
                        {subject.name}
                    </option>
                {/each}
            </Select>
        {/if}
        {#if educationalLevels && educationalLevels.length > 1 }
            <Select label={lang.main('Course_catalogue_age_group')} bind:value={courses.currentFilter.educationalLevelId} labelPosition='top' on:change={() => search(true)}>
                {#each educationalLevels as educationalLevel}
                    <option value={educationalLevel.id}>
                        {educationalLevel.name}
                    </option>
                {/each}
            </Select>
        {/if}
        {#if targetRoles && targetRoles.length > 1}
            <ListBoxDropdown
                labelPosition='top'
                label={lang.main('Course_catalogue_Target_role')}
                items={getRolesForFilter()}
                bind:selectedValues={selectedRoles}
                applyText={lang.main('Apply')}
                selectedValueText={selectedRoleText}
                on:change={() => search(true)} />
        {/if}
        {#if organisations && organisations.length > 1}
            <Select label={lang.main('Organisation')} bind:value={courses.currentFilter.organisationId} labelPosition='top' on:change={() => search(true)}>
                {#each organisations as organisation}
                    <option value={organisation.id}>
                        {organisation.name}
                    </option>
                {/each}
            </Select>
        {/if}
    </div>

    <CourseList
        list={courses.courses}
        totalCount={courses.totalCount}
        {filterEnabled}
        {loadInProgress}
    />
    
    {#if courses.totalCount > courses.courses.length && !loadInProgress}
        <div class="its-course-catalogue-coursecards-showmore">
            <Button on:click={loadMore} text={lang.main('Show_more')} type='secondary'/>
        </div>
    {/if}
</Page>

<script lang="ts">
    import Header from "../Header/Header.svelte";
    import Banner from "@itslearning/prometheus/assets/feedback/Banner/v1/Banner.svelte";
    import Button from '@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte';
    import CourseList from "../CourseList/CourseList.svelte";
    import Page from "@itslearning/prometheus/assets/templates/Page/v1/Page.svelte";
    import PageHeader from '@itslearning/prometheus/assets/patterns/PageHeader/v1/PageHeader.svelte';
    import Select from '@itslearning/prometheus/assets/inputs/Select/v1/Select.svelte';
    import SearchInput from "@itslearning/prometheus/assets/inputs/SearchInput/v1/SearchInput.svelte";
    import ListBoxDropdown from "@itslearning/prometheus/assets/inputs/ListBoxDropdown/v1/ListBoxDropdown.svelte";
    
    import { formatLanguageString } from '@itslearning/atlas/strings/format.js';
    import { debounce } from '@itslearning/atlas/dom/Ratecontrol';
    import { createSearchCourses } from "../../api/api.js";

    import { Keys } from "@itslearning/atlas/keyboard/keys";
    import { normalizeKey } from "@itslearning/atlas/keyboard/normalize";
    import { lang } from "@its-ui/mono/lang/provider";
    import type { AntiForgery, FilterOption, CatalogueCoursesModel } from '../../api/api.ts';
    import { onMount } from "svelte";

    export let backUrl: string = '';
    export let courses: CatalogueCoursesModel;
    export let targetRoles: FilterOption[] = [];
    export let subjects: FilterOption[] = [];
    export let educationalLevels: FilterOption[] = [];
    export let organisations: FilterOption[] = [];
    export let applicationRejectedCourseTitle: string;
    export let isExternal: boolean;
    export let antiForgery: AntiForgery;

    const searchCourses = createSearchCourses(isExternal);

    const delayedSearch = debounce(async () => {
        await search(true); 
        searchInputRef.querySelector('input').focus();
    }, 500, null);


    let infoFeedback = '';
    let errorFeedback = '';
    let loadInProgress = false;
    let searchInputRef;
    let lastSearchText = courses.currentFilter.searchText;
    let abortController = null;
    let selectedRoles = [];

    const TARGET_ROLES_FILTER_QUERY_STRING_PARAMETER = "TargetRoles";
    const SUBJECT_FILTER_QUERY_STRING_PARAMETER = "SubjectId";
    const EDUCATIONAL_LEVEL_FILTER_QUERY_STRING_PARAMETER = "EducationalLevelId";
    const ORGANISATION_FILTER_QUERY_STRING_PARAMETER = "OrganisationId";
    const SEARCH_TEXT_QUERY_STRING_PARAMETER = "SearchText";

    $: {
        if (applicationRejectedCourseTitle) {
            infoFeedback = formatLanguageString(lang.main('Course_catalogue_application_has_not_been_acceppted'), applicationRejectedCourseTitle);
        }
    }
    
    $: filterEnabled = !!courses.currentFilter.searchText
        || Number(courses.currentFilter.educationalLevelId) !== 0
        || Number(courses.currentFilter.subjectId) !== 0
        || Number(courses.currentFilter.targetRole) !== 0
        || Number(courses.currentFilter.organisationId) !== 0;

    $: selectedRoleText = getSelectedRolesText(targetRoles, selectedRoles);

    onMount(() => {
        const roles = getRolesForFilter();
        if (roles.length > 1) {
            selectedRoles = courses.currentFilter.targetRoles?.length > 0
                ? courses.currentFilter.targetRoles
                : roles.filter(e => !e.selectAll).map(e => e.value);
        }
    });

    async function loadMore(): Promise<void> {
        await search(false);
    }

    async function onSearchKeyUp(event): Promise<void>{
        const key = normalizeKey(event.key);

        if (key === Keys.TAB || key === Keys.SHIFT) {
            return;
        }

        if (courses.currentFilter.searchText !== lastSearchText) {
            if (key === Keys.ENTER) {
                await search(true);
            } else {
                delayedSearch();
            }
        }
    }
    
    async function onSearchClear(): Promise<void> {
        await search(true);
    }

    async function search(resetPageIndex): Promise<void> {
        errorFeedback = '';

        // Cancel previous request if it exists
        if (abortController) {
            abortController.abort();
        }

        // Create new AbortController for this request
        abortController = new AbortController();
        const signal = abortController.signal;

        if (resetPageIndex) {
            courses.currentFilter.paging.pageIndex = 0;
            courses.courses = [];
        } else {
            courses.currentFilter.paging.pageIndex++;
        }

        courses.currentFilter.targetRoles =
            selectedRoles.length > 0 && targetRoles.length != selectedRoles.length
            ? selectedRoles : [];

        // Update URL params (after debounce, before async call)
        updateUrlParams();

        // Sync lastSearchText to current value so subsequent keystrokes can be compared correctly
        lastSearchText = courses.currentFilter.searchText;

        loadInProgress = true;

        const currentList = [...courses.courses];

        try {
            // Store result separately to preserve courses.currentFilter binding and prevent user input loss
            const searchResults = await searchCourses(courses.currentFilter, antiForgery, signal);

            // Only update UI if request hasn't been aborted
            if (!signal.aborted) {
                courses.courses = [...currentList, ...searchResults.courses];
                courses.totalCount = searchResults.totalCount;
                loadInProgress = false;
            }
        } catch (error) {
            // Don't show error if request was aborted
            if (error.name === 'AbortError') {
                return;
            }

            errorFeedback = lang.main('Error_occured');
            loadInProgress = false;
        }
    }

    function getRolesForFilter() {
        const options = [
            {
                value: "all",
                label: lang.main('All'),
                selectAll: true
            },
        ];

        if (targetRoles && Array.isArray(targetRoles)) {
            for (const targetRole of targetRoles) {
                options.push({
                    value: targetRole.id,
                    label: targetRole.name,
                    selectAll: false
                });
            }
        }

        return options;
    }

    function getSelectedRolesText(roles, selectedRoles) {
        if (!roles || !selectedRoles) {
            return lang.main('All');
        }

        const allSelected = roles.length === selectedRoles.length || selectedRoles.length === 0;

        if (allSelected) {
            return lang.main('All');
        }

        return selectedRoles.length === 1
            ? roles.find(e => e.id === selectedRoles[0]).name
            : `${selectedRoles.length} ${lang.main('Broadcasting_Roles').toLowerCase()}`;
    }

    function updateUrlParams(): void {
        const url = new URL(window.location.href);
        url.searchParams.delete(TARGET_ROLES_FILTER_QUERY_STRING_PARAMETER);

        if (subjects) {
            url.searchParams.set(SUBJECT_FILTER_QUERY_STRING_PARAMETER, courses.currentFilter.subjectId);
        }

        if (targetRoles) {
            courses.currentFilter.targetRoles.forEach(role => {
                url.searchParams.append(TARGET_ROLES_FILTER_QUERY_STRING_PARAMETER, role);
            });
        }

        if (educationalLevels) {
            url.searchParams.set(EDUCATIONAL_LEVEL_FILTER_QUERY_STRING_PARAMETER, courses.currentFilter.educationalLevelId);
        }

        if (organisations) {
            url.searchParams.set(ORGANISATION_FILTER_QUERY_STRING_PARAMETER, courses.currentFilter.organisationId);
        }

        url.searchParams.set(SEARCH_TEXT_QUERY_STRING_PARAMETER, courses.currentFilter.searchText);

        window.history.pushState({}, "", url);
    }
</script>
